import { AxisBottom, AxisLeft } from '@visx/axis'
import { curveMonotoneX } from '@visx/curve'
import { Group } from '@visx/group'
import { scaleLinear, scaleTime } from '@visx/scale'
import { AreaClosed, LinePath } from '@visx/shape'
import { memo, useCallback, useMemo, useState } from 'react'

import { formatAxisTime, formatClock, formatCompact, formatInt } from '../../lib/format.ts'
import type { ThroughputPoint } from '../../lib/sse/types.ts'
import { useElementSize } from './useElementSize.ts'

const MARGIN = { top: 6, right: 4, bottom: 18, left: 42 }

interface Sample {
  time: number
  value: number
}

interface HoverState {
  index: number
  x: number
}

const axisLabelProps = () =>
  ({
    fill: 'var(--c-axis)',
    fontSize: 10,
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
  }) as const

/** Index of the sample nearest a given time. Plain binary search, no d3 needed. */
function nearestIndex(samples: readonly Sample[], time: number): number {
  let low = 0
  let high = samples.length - 1
  while (low < high) {
    const mid = (low + high) >> 1
    const sample = samples[mid]
    if (sample === undefined) break
    if (sample.time < time) low = mid + 1
    else high = mid
  }
  const current = samples[low]
  const previous = samples[low - 1]
  if (current !== undefined && previous !== undefined) {
    return Math.abs(previous.time - time) <= Math.abs(current.time - time) ? low - 1 : low
  }
  return low
}

interface ThroughputChartProps {
  points: readonly ThroughputPoint[]
  /** Drawn as a dashed rule — the window's own mean, not a target. */
  mean: number
  height?: number
}

/**
 * Throughput over the retained window.
 *
 * One series, one hue. The area exists to make the shape readable at a glance
 * across a room; the line carries the precision. There is no second series here
 * because the payload has one — accepted and rejected live as numbers in the
 * ingest panel rather than as invented lines.
 */
export const ThroughputChart = memo(function ThroughputChart({
  points,
  mean,
  height = 150,
}: ThroughputChartProps) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const [hover, setHover] = useState<HoverState | null>(null)

  const samples = useMemo<Sample[]>(
    () => points.map((point) => ({ time: Date.parse(point.t), value: point.events_per_sec })),
    [points],
  )

  const width = size.width
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right)
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom)

  const { xScale, yScale, peak } = useMemo(() => {
    const first = samples[0]
    const last = samples[samples.length - 1]
    const domainStart = first?.time ?? 0
    const domainEnd = last?.time ?? 1
    let max = 0
    for (const sample of samples) if (sample.value > max) max = sample.value

    return {
      peak: max,
      xScale: scaleTime<number>({
        domain: [new Date(domainStart), new Date(domainEnd)],
        range: [0, innerWidth],
      }),
      // Zero-based on purpose: a truncated axis exaggerates every wobble, and
      // this chart is read for the size of a deviation, not its existence.
      yScale: scaleLinear<number>({
        domain: [0, max === 0 ? 1 : max * 1.12],
        range: [innerHeight, 0],
        nice: true,
      }),
    }
  }, [samples, innerWidth, innerHeight])

  const onMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      if (samples.length === 0) return
      const bounds = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - bounds.left
      const time = xScale.invert(x).valueOf()
      setHover({ index: nearestIndex(samples, time), x })
    },
    [samples, xScale],
  )

  const onLeave = useCallback(() => setHover(null), [])

  const hovered = hover === null ? undefined : samples[hover.index]

  const summary = useMemo(() => {
    const first = samples[0]
    const last = samples[samples.length - 1]
    if (first === undefined || last === undefined) return 'Série de throughput vazia.'
    return `Throughput de ${formatClock(new Date(first.time).toISOString())} a ${formatClock(
      new Date(last.time).toISOString(),
    )} UTC. Atual ${formatInt(last.value)} eventos por segundo, pico ${formatInt(peak)}, média ${formatInt(mean)}.`
  }, [samples, peak, mean])

  return (
    <div ref={ref} className="live-chart relative w-full" style={{ height }}>
      {width > 0 && samples.length > 1 && (
        <svg width={width} height={height} role="img" aria-label={summary}>
          <Group left={MARGIN.left} top={MARGIN.top}>
            {yScale.ticks(3).map((tick) => (
              <line
                key={tick}
                x1={0}
                x2={innerWidth}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke="var(--c-grid)"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
            ))}

            <AreaClosed<Sample>
              data={samples}
              x={(d) => xScale(d.time)}
              y={(d) => yScale(d.value)}
              yScale={yScale}
              curve={curveMonotoneX}
              fill="var(--c-series-fill)"
            />
            <LinePath<Sample>
              data={samples}
              x={(d) => xScale(d.time)}
              y={(d) => yScale(d.value)}
              curve={curveMonotoneX}
              stroke="var(--c-series-1)"
              strokeWidth={1.5}
              strokeLinejoin="round"
            />

            {mean > 0 && (
              <line
                x1={0}
                x2={innerWidth}
                y1={yScale(mean)}
                y2={yScale(mean)}
                stroke="var(--c-border-strong)"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )}

            {hovered !== undefined && (
              <g>
                <line
                  x1={xScale(hovered.time)}
                  x2={xScale(hovered.time)}
                  y1={0}
                  y2={innerHeight}
                  stroke="var(--c-border-strong)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <circle
                  cx={xScale(hovered.time)}
                  cy={yScale(hovered.value)}
                  r={2.5}
                  fill="var(--c-series-1)"
                />
              </g>
            )}

            <AxisLeft
              scale={yScale}
              numTicks={3}
              hideAxisLine
              hideTicks
              tickFormat={(value) => formatCompact(Number(value))}
              tickLabelProps={axisLabelProps}
            />
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              numTicks={Math.max(2, Math.floor(innerWidth / 110))}
              stroke="var(--c-border-subtle)"
              hideTicks
              tickFormat={(value) => formatAxisTime(new Date(Number(value)))}
              tickLabelProps={axisLabelProps}
            />

            <rect
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onPointerMove={onMove}
              onPointerLeave={onLeave}
            />
          </Group>
        </svg>
      )}

      {hover !== null && hovered !== undefined && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 bg-raised px-1.5 py-0.5 text-micro whitespace-nowrap shadow-overlay"
          style={{
            left: Math.min(Math.max(hover.x + MARGIN.left - 40, 0), Math.max(0, width - 120)),
          }}
        >
          <span className="tnum font-mono text-fg">{formatInt(hovered.value)}</span>
          <span className="text-fg-muted"> ev/s · </span>
          <span className="tnum font-mono text-fg-secondary">
            {formatClock(new Date(hovered.time).toISOString())}
          </span>
        </div>
      )}

      {samples.length <= 1 && (
        <p className="p-2 text-sm text-fg-muted">Aguardando pontos suficientes para a série.</p>
      )}
    </div>
  )
})
